import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { useFormikContext } from 'formik';
import { Location } from 'history';
import { Prompt, useHistory } from 'react-router-dom';

export interface PromptLeaveProps {
  title: React.ReactNode;
  message: React.ReactNode;
  shouldBlockOnLocation?: (location: Location) => boolean;
  enabled?: boolean;
}

export const PromptLeave: React.FC<PromptLeaveProps> = (props) => {
  const { title, message, shouldBlockOnLocation, enabled = !import.meta.env.DEV } = props;

  const classes = useCommonStyles();

  const [dialogOpen, setDialogOpen] = useState(false);

  const formik = useFormikContext();

  const unblockRef = useRef<() => void>();

  const history = useHistory();

  const shouldBlock = formik.dirty && !formik.isSubmitting && enabled;

  useEffect(() => {
    if (shouldBlock) {
      function promptUnload(this: Window, e: BeforeUnloadEvent) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }

      window.addEventListener('beforeunload', promptUnload);

      return () => {
        window.removeEventListener('beforeunload', promptUnload);
      };
    }
  }, [shouldBlock]);

  return (
    <>
      <Prompt
        when={shouldBlock && !dialogOpen}
        message={(location, action) => {
          if (shouldBlockOnLocation && !shouldBlockOnLocation(location)) {
            return true;
          }
          setDialogOpen(true);
          unblockRef.current = () => {
            switch (action) {
              case 'POP':
                history.goBack();
                break;
              case 'PUSH':
                history.push(location);
                break;
              case 'REPLACE':
                history.replace(location);
                break;
            }
          };
          return false;
        }}
      />
      <ActionableDialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <ActionableDialogTitle>{title}</ActionableDialogTitle>
        <ActionableDialogContent className={classes.middleTextImportant}>
          {message}
        </ActionableDialogContent>
        <ActionableDialogActions>
          <Button
            color="primary"
            text={formStrings.ok}
            onClick={() => {
              unblockRef.current?.();
              setDialogOpen(false);
            }}
            type="button"
            size="small"
          />
        </ActionableDialogActions>
      </ActionableDialog>
    </>
  );
};
